import { t } from 'i18next';

import { formatCorporateActionDate } from '../corporate-action-item/utils';
import { formatDateRange } from '../economic-calendar-item/utils';
import {
  formatScenarioDescription,
  resolveScenarioEntityTag,
} from '../scenario-item/formatScenarioDescription';
import type {
  ConversationInventory,
  FormattedConversationInventory,
} from '../services/inventory-service';
import { htmlNodes } from './htmlNodes';
import {
  type KgExportGroup,
  kgExportGroupsToHtml,
  kgExportGroupsToPlainText,
  resolveKgForSourcesSection,
} from './kgSourcesForCopy';

type SourcesSection = {
  title: string;
  textItems: string[];
  htmlItems: string[];
  kgExportGroups?: KgExportGroup[];
};

export type InventoryContent = { html: string; text: string };

const getIpoEventLines = (
  inventory: ConversationInventory | FormattedConversationInventory
): string[] =>
  inventory['ipo-event']?.items?.map(i => `${i.name} (${i.ticker})`) ?? [];

const getScenarioLines = (
  inventory: ConversationInventory | FormattedConversationInventory,
  isEnabled: boolean
): string[] => {
  if (!isEnabled) {
    return [];
  }

  const scenarioEntityNames =
    'scenarioEntityNames' in inventory
      ? inventory.scenarioEntityNames
      : undefined;

  return (
    inventory.analysis?.items?.map(item => {
      const entityTag = resolveScenarioEntityTag(item);
      return formatScenarioDescription(
        item.description,
        entityTag ? scenarioEntityNames?.[entityTag] : undefined
      );
    }) ?? []
  );
};

const getCorporateActionLines = (
  inventory: ConversationInventory | FormattedConversationInventory,
  isEnabled: boolean
): string[] => {
  if (!isEnabled) {
    return [];
  }

  return (
    inventory['corporate-action']?.items?.map(
      item =>
        `${item.tag} · ${item.action_type} · ${formatCorporateActionDate(
          item.effective_date
        )}`
    ) ?? []
  );
};

export interface BuildInventoryContentParams {
  inventory?: ConversationInventory | FormattedConversationInventory;
  isCorporateActionInventoryEnabled?: boolean;
  isScenarioInventoryEnabled?: boolean;
}

export const buildInventoryContent = async ({
  inventory,
  isCorporateActionInventoryEnabled = false,
  isScenarioInventoryEnabled = false,
}: BuildInventoryContentParams = {}): Promise<InventoryContent> => {
  if (!inventory || Object.keys(inventory).length === 0) {
    return { html: '', text: '' };
  }

  const { kgExportGroups } = await resolveKgForSourcesSection(inventory);

  let html = '';
  let text = '';

  const entities = inventory?.entity?.items?.map(i => i.tag) ?? [];
  const timeseries = inventory?.timeseries?.items?.map(i => i.snake) ?? [];
  const webLinks =
    inventory?.['web-links']?.items?.flatMap(i => i.urls ?? []) ?? [];
  const financialStatements =
    inventory?.['financial-statement']?.items?.map(i => i.tag) ?? [];
  const externalData =
    inventory?.['external-data']?.items?.flatMap(i => i.links ?? []) ?? [];
  const ipoEvents = getIpoEventLines(inventory);
  const corporateActions = getCorporateActionLines(
    inventory,
    isCorporateActionInventoryEnabled
  );
  const scenarioLines = getScenarioLines(inventory, isScenarioInventoryEnabled);

  const economicCalendarLines = (
    inventory?.['economic-calendar']?.items ?? []
  ).map(item => {
    const { formattedFrom, formattedTo } = formatDateRange(
      item.from ?? '',
      item.to ?? ''
    );
    const dateRange = `${formattedFrom} - ${formattedTo}`;

    if ((item.countries?.length ?? 0) > 1) {
      return `${t('calendar:economicEventsMultiple')} · ${
        item.countries?.join(', ') ?? ''
      } · ${dateRange}`;
    }

    if (!item.countries?.length && !item.categories?.length) {
      return `${t('calendar:allEconomicEvents')} · ${dateRange}`;
    }

    if (!item.countries?.length && item.categories?.length) {
      return `${item.categories.join(', ')} · ${dateRange}`;
    }

    const countryName = item.countries?.[0]
      ? t(`countries:${item.countries[0]}`, { defaultValue: item.countries[0] })
      : '';
    return `${t('calendar:economicEvents', {
      country: countryName,
    })} · ${dateRange}`;
  });

  const earningsCalendarLines = (
    inventory?.['earnings-calendar']?.items ?? []
  ).map(item => {
    const { formattedFrom, formattedTo } = formatDateRange(
      item.from ?? '',
      item.to ?? ''
    );
    const dateRange = `${formattedFrom} - ${formattedTo}`;
    const itemEntities = item.entities ?? [];

    if (itemEntities.length) {
      return `${itemEntities.join(', ')} · ${dateRange}`;
    }

    return `${t('calendar:earnings')} · ${dateRange}`;
  });

  const earningsHistoryLines = (
    inventory?.['earnings-history']?.items ?? []
  ).map(item => {
    const { formattedFrom, formattedTo } = formatDateRange(
      item.from ?? '',
      item.to ?? ''
    );
    const dateRange = `${formattedFrom} - ${formattedTo}`;

    return `${item.entity_tag} · ${dateRange}`;
  });

  const documentSearchItems =
    inventory?.['document-search']?.items?.filter(
      item => (item.documents?.length ?? 0) > 0
    ) ?? [];

  const newsHeadlines = inventory?.news?.items?.map(item => item.title) ?? [];

  const citationLines = documentSearchItems.map(item => {
    const rawQuestion = item.question ?? '';
    const question = rawQuestion ? `"${rawQuestion}"` : '';
    const count = item.documents?.length ?? 0;
    return t('chat:inventory.citationsForQuestionFoundDocuments', {
      question,
      count,
    });
  });

  const knowledgeGraphSections: SourcesSection[] =
    kgExportGroups != null && kgExportGroups.length > 0
      ? [
          {
            title: t('chat:inventory.sections.knowledgeGraph'),
            textItems: [],
            htmlItems: [],
            kgExportGroups,
          },
        ]
      : [];

  const sections: SourcesSection[] = [
    {
      title: t('chat:inventory.sections.entities', { count: entities.length }),
      textItems: entities,
      htmlItems: entities,
    },
    {
      title: t('chat:inventory.sections.timeSeries', {
        count: timeseries.length,
      }),
      textItems: timeseries,
      htmlItems: timeseries,
    },
    {
      title: t('chat:inventory.sections.financials', {
        count: financialStatements.length,
      }),
      textItems: financialStatements,
      htmlItems: financialStatements,
    },
    {
      title: t('chat:inventory.sections.citations', {
        count: citationLines.length,
      }),
      textItems: citationLines,
      htmlItems: citationLines,
    },
    {
      title: t('chat:inventory.sections.news', {
        count: newsHeadlines.length,
      }),
      textItems: newsHeadlines,
      htmlItems: newsHeadlines,
    },
    {
      title: t('chat:inventory.sections.externalData', {
        count: externalData.length,
      }),
      textItems: externalData,
      htmlItems: externalData
        .filter((url): url is string => url != null)
        .map(url => htmlNodes.a(url, url)),
    },
    {
      title: t('chat:inventory.sections.webLinks', { count: webLinks.length }),
      textItems: webLinks,
      htmlItems: webLinks
        .filter((url): url is string => url != null)
        .map(url => htmlNodes.a(url, url)),
    },
    {
      title: t('chat:inventory.sections.economicCalendar', {
        count: economicCalendarLines.length,
      }),
      textItems: economicCalendarLines,
      htmlItems: economicCalendarLines,
    },
    {
      title: t('chat:inventory.sections.corporateActions', {
        count: corporateActions.length,
      }),
      textItems: corporateActions,
      htmlItems: corporateActions,
    },
    {
      title: t('chat:inventory.sections.earningsCalendar', {
        count: earningsCalendarLines.length,
      }),
      textItems: earningsCalendarLines,
      htmlItems: earningsCalendarLines,
    },
    {
      title: t('chat:inventory.sections.earningsHistory', {
        count: earningsHistoryLines.length,
      }),
      textItems: earningsHistoryLines,
      htmlItems: earningsHistoryLines,
    },
    {
      title: t('chat:inventory.sections.ipoCalendar', {
        count: ipoEvents.length,
      }),
      textItems: ipoEvents,
      htmlItems: ipoEvents,
    },
    {
      title: t('chat:inventory.sections.scenario', {
        count: scenarioLines.length,
      }),
      textItems: scenarioLines,
      htmlItems: scenarioLines,
    },
    ...knowledgeGraphSections,
  ];

  const nonEmpty = sections.filter(
    s =>
      (s.kgExportGroups != null && s.kgExportGroups.length > 0) ||
      s.textItems.length > 0
  );

  if (!nonEmpty.length) {
    return { html, text };
  }

  const citationsNote = t('chat:citationsNote');
  text += `\n\n## ${t('chat:inventory.sources')}\n\n${citationsNote}`;

  html += htmlNodes.h2(t('chat:inventory.sources'));
  html += `<p style="font-size: 0.875rem; margin-bottom: 8px;">${citationsNote}</p>`;

  nonEmpty.forEach(s => {
    if (s.kgExportGroups != null && s.kgExportGroups.length > 0) {
      text +=
        `\n\n### ${s.title}\n` + kgExportGroupsToPlainText(s.kgExportGroups);
      html += htmlNodes.h3(s.title) + kgExportGroupsToHtml(s.kgExportGroups);
      return;
    }

    text += `\n\n### ${s.title}\n` + s.textItems.map(v => `- ${v}`).join('\n');
    html += htmlNodes.h3(s.title);
    html += htmlNodes.unorderedList(
      s.htmlItems
        .filter(v => v !== null && v !== undefined)
        .map(v => htmlNodes.unorderedListItem(v))
        .join('')
    );
  });

  return { html, text };
};
